import React, { FC, useEffect, useState } from 'react'
import { Grid, Stack, Text } from '@lojinha/design-system'
import styled from 'styled-components'
import { DeliveryStatus, PaymentStatus } from '@lojinha/palantir'
import { SectionBanner } from '../../components/section-banner'
import { NoPageComponent } from '../page-content/no-page.ui'
import { labels } from '../../dictionary/pt-labels'
import { useMyPurchases } from './hook'
import { LoadingPurchases } from './loading.ui'
import { PurchaseType } from './type'
import { PurchaseCardListItem } from './card.ui'

const CustomGrid = styled(Grid)(
  ({ theme }) => `
  margin-top: ${theme.space.x32};
  margin-bottom: ${theme.space.x32};
  
    ${theme.media.below('md')} {
      margin-top: ${theme.space.x24};
      margin-bottom: ${theme.space.x24};
    }
`
)
const today = new Date()

export const MyPurchases: FC = () => {
  const { loading, purchases } = useMyPurchases()
  const [inProgressList, setInProgressList] = useState<PurchaseType[]>([])
  const [previousList, setPreviousList] = useState<PurchaseType[]>([])

  const filterInPreviousList = (item: PurchaseType) => {
    const createdAtDate = new Date(item.createdAt)
    return (
      item.deliveryStatus === DeliveryStatus.Delivered ||
      (item.paymentStatus === PaymentStatus.Canceled &&
        today.getDate() - createdAtDate.getDate() > 3)
    )
  }

  const filterInProgressList = (item: PurchaseType) => {
    const createdAtDate = new Date(item.createdAt)
    return (
      (item.deliveryStatus !== DeliveryStatus.Delivered &&
        item.paymentStatus !== PaymentStatus.Canceled) ||
      (item.paymentStatus === PaymentStatus.Canceled &&
        today.getDate() - createdAtDate.getDate() <= 3)
    )
  }

  useEffect(() => {
    const sortedList: PurchaseType[] | [] = purchases
      ?.slice()
      ?.sort((a: PurchaseType, b: PurchaseType) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })

    const inProgress = sortedList?.filter((item: PurchaseType) =>
      filterInProgressList(item)
    )

    const previous = sortedList?.filter((item: PurchaseType) =>
      filterInPreviousList(item)
    )

    setPreviousList(previous)
    setInProgressList(inProgress)
  }, [loading])

  if (loading) return <LoadingPurchases />

  if (!purchases?.length)
    return (
      <NoPageComponent
        text={labels.emptyPurchasesList}
        title={labels.emptyPurchasesListTitle}
      />
    )

  const renderCard = (item: PurchaseType, index: number) => {
    return <PurchaseCardListItem tabindex={0} key={index} item={item} />
  }

  return (
    <Grid gridTemplateColumns={'repeat(12, 1fr)'}>
      <Grid gridColumn={['1 / 13']}>
        <SectionBanner />
      </Grid>
      {inProgressList?.length > 0 && (
        <CustomGrid gridColumn={['2 / 12']}>
          <Stack as={'ul'}>
            <Text textSize="x2" textTransform={'uppercase'} isBold>
              {labels.inProgress}
            </Text>
            {inProgressList.map(renderCard)}
          </Stack>
        </CustomGrid>
      )}
      {previousList?.length > 0 && (
        <CustomGrid gridColumn={['2 / 12']}>
          <Stack as={'ul'}>
            <Text textSize="x2" textTransform={'uppercase'} isBold>
              {labels.previousPurchases}
            </Text>
            {previousList.map(renderCard)}
          </Stack>
        </CustomGrid>
      )}
    </Grid>
  )
}
